import Card from '@/components/common/Card';
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { useState } from 'react';
import TodayAttendance from '@/components/common/TodayAttendance';
import Chart from 'react-apexcharts';
import { MapPin, Clock, Calendar, CheckCircle2 } from 'lucide-react';

interface StatCardProps {
    title: string;
    value: number;
    color: string;
    showSubtitle: boolean;
    subtitleText?: string;
}

const UserDashboard: React.FC = () => {

    const [attendanceChartOptions] = useState({
        chart: {
            type: 'bar',
            toolbar: { show: false },
            fontFamily: 'inherit',
            parentHeightOffset: 0
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                columnWidth: '40%',
            }
        },
        colors: ['#10b981', '#f43f5e'],
        dataLabels: { enabled: false },
        xaxis: {
            categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: { show: false },
        grid: {
            strokeDashArray: 4,
            yaxis: { lines: { show: true } },
            padding: { top: 0, right: 0, bottom: 0, left: 10 }
        },
        legend: { show: true, position: 'top', horizontalAlign: 'right' }
    });

    const [attendanceChartSeries] = useState([
        { name: 'Present', data: [14, 15, 13, 16, 14, 10, 0] },
        { name: 'Absent/Leave', data: [4, 3, 5, 2, 4, 8, 18] }
    ]);

    const [stats] = useState<StatCardProps[]>([
        {
            title: "Total Employees",
            value: 18,
            color: "rgb(15, 23, 42)",
            showSubtitle: false
        },
        {
            title: "Present Today",
            value: 14,
            color: "rgb(22, 163, 74)",
            showSubtitle: true,
            subtitleText: "of 18"
        },
        {
            title: "On Leave",
            value: 2,
            color: "rgb(14, 165, 233)",
            showSubtitle: false
        },
        {
            title: "Pending Approvals",
            value: 3,
            color: "rgb(217, 119, 6)",
            showSubtitle: false
        }
    ]);
    const [workforceAnalysis] = useState([
        {
            title: "Average Age",
            value: "34 yrs",
            color: "rgb(15, 23, 42)",
            subtitle: "across 18 staff"
        },
        {
            title: "Avg Experience",
            value: "6.2 yrs",
            color: "rgb(15, 23, 42)",
            subtitle: "across 18 staff"
        },
        {
            title: "Turnover Ratio",
            value: "0%",
            color: "rgb(22, 163, 74)",
            subtitle: "Dec 2025"
        },
        {
            title: "Gender Ratio",
            value: "F 55% · M 45%",
            color: "rgb(15, 23, 42)",
            subtitle: "10 F · 8 M"
        }
    ]);

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading title='Dashboard' />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {stats.map((stat, index) => (
                        <Card key={index} >
                            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                                {stat.title}
                            </div>
                            <div
                                className="mt-2 font-display text-2xl font-semibold"
                                style={{ color: stat.color }}
                            >
                                {stat.value}
                            </div>
                            {stat.showSubtitle && (
                                <div className="mt-1 text-xs text-slate-500">
                                    {stat.subtitleText}
                                </div>
                            )}
                        </Card>
                    ))}
                </div>

                {/* Attendance */}
                <div className="grid grid-cols-1 lg:grid-cols-[400px_2fr] gap-6">
                    {/* Live Tracking Card */}
                    <TodayAttendance />

                    {/* Chart & Actions */}
                    <div className=" flex flex-col gap-6">
                        <Card className="flex-1 p-5 border border-slate-200/60 shadow-sm flex flex-col">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-display text-base font-semibold text-slate-800">Weekly Attendance Trend</h3>
                                <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full flex items-center gap-1.5 border border-emerald-200">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> 82% Avg
                                </span>
                            </div>
                            <div className="flex-1 w-full min-h-[220px]">
                                <Chart options={attendanceChartOptions as any} series={attendanceChartSeries} type="bar" height="100%" />
                            </div>
                        </Card>

                        {/* Quick Actions */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <Card className="hover:border-emerald-500/50 hover:shadow-md transition-all cursor-pointer group bg-linear-to-b from-white to-slate-50/50 p-4 border border-slate-200/60 shadow-sm">
                                <div className="flex flex-col items-center justify-center text-center">
                                    <div className="w-12 h-12 rounded-2xl bg-emerald-100/80 text-emerald-600 flex items-center justify-center mb-3 group-hover:scale-110 group-hover:bg-emerald-100 transition-all shadow-sm">
                                        <Calendar className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-sm font-semibold text-slate-800">Apply Leave</h3>
                                    <p className="text-[11px] text-slate-500 mt-1 font-medium bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200/60">2 Pending</p>
                                </div>
                            </Card>
                            <Card className="hover:border-amber-500/50 hover:shadow-md transition-all cursor-pointer group bg-linear-to-b from-white to-slate-50/50 p-4 border border-slate-200/60 shadow-sm">
                                <div className="flex flex-col items-center justify-center text-center">
                                    <div className="w-12 h-12 rounded-2xl bg-amber-100/80 text-amber-600 flex items-center justify-center mb-3 group-hover:scale-110 group-hover:bg-amber-100 transition-all shadow-sm">
                                        <Clock className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-sm font-semibold text-slate-800">Regularisation</h3>
                                    <p className="text-[11px] text-slate-500 mt-1 font-medium bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200/60">1 Request</p>
                                </div>
                            </Card>
                            <Card className="hover:border-blue-500/50 hover:shadow-md transition-all cursor-pointer group bg-linear-to-b from-white to-slate-50/50 p-4 border border-slate-200/60 shadow-sm">
                                <div className="flex flex-col items-center justify-center text-center">
                                    <div className="w-12 h-12 rounded-2xl bg-blue-100/80 text-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 group-hover:bg-blue-100 transition-all shadow-sm">
                                        <MapPin className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-sm font-semibold text-slate-800">Work from Home</h3>
                                    <p className="text-[11px] text-slate-500 mt-1 font-medium bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200/60">Apply Now</p>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>





                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                    <Card>
                        <h3 className="font-display mb-4 text-base font-semibold">Department Headcount</h3>

                    </Card>
                    <Card>
                        <h3 className="font-display mb-4 text-base font-semibold">Credential Alerts</h3>
                    </Card>
                </div>
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                    <Card>
                        <h3 className="font-display mb-4 text-base font-semibold">Recent Leave Requests</h3>

                    </Card>
                    <Card>
                        <h3 className="font-display mb-4 text-base font-semibold">Attendance Trend (last 7 days)</h3>
                    </Card>
                </div>
                <Card>
                    <h3 className="font-display mb-4 text-base font-semibold">Team Wellness Today</h3>
                </Card>
                <h2 className="font-display text-lg font-semibold text-slate-800 mb-4">Workforce Analytics</h2>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    {workforceAnalysis.map((stat, index) => (
                        <Card
                            key={index}
                        >
                            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                                {stat.title}
                            </div>
                            <div
                                className="mt-2 font-display text-2xl font-semibold"
                                style={{ color: stat.color }}
                            >
                                {stat.value}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                                {stat.subtitle}
                            </div>
                        </Card>
                    ))}
                </div>
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                    <Card>
                        <h3 className="font-display mb-4 text-base font-semibold">Department-wise Gender Distribution</h3>

                    </Card>
                    <Card>
                        <h3 className="font-display mb-4 text-base font-semibold">Staff by Designation Level</h3>
                    </Card>
                </div>
            </div>
        </Layout>
    )
}

export default UserDashboard